/**
 * useViewerTimeline — Runtime viewer hook.
 *
 * Fetches GET /api/viewer/:companyId/:productId/timeline and exposes
 * the live release's timeline steps, visibility assignments, and last
 * frame so the public viewer can hydrate without admin credentials.
 *
 * Status states:
 *   - 'idle'    — companyId or productId is null (no fetch attempted)
 *   - 'loading' — request in flight
 *   - 'ready'   — 200 OK, payload received
 *   - 'missing' — 404 (no live release published yet)
 *   - 'error'   — network failure or non-404 server error
 *
 * Caches the result for the session lifetime per (companyId, productId).
 */

import { useEffect, useRef, useState } from 'react';
import type { StepObjectVisibility, TimelineStep } from '../types/timeline';

export type ViewerTimelineStatus = 'idle' | 'loading' | 'ready' | 'missing' | 'error';

export interface UseViewerTimelineResult {
  steps: TimelineStep[];
  visibilityAssignments: StepObjectVisibility[];
  lastFrame: number;
  status: ViewerTimelineStatus;
}

interface CachedTimelineEntry {
  steps: TimelineStep[];
  visibilityAssignments: StepObjectVisibility[];
  lastFrame: number;
  status: ViewerTimelineStatus;
}

const sessionCache = new Map<string, CachedTimelineEntry>();

function cacheKey(companyId: string, productId: string): string {
  return `${companyId}::${productId}`;
}

function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || '';
}

const EMPTY_RESULT: UseViewerTimelineResult = {
  steps: [],
  visibilityAssignments: [],
  lastFrame: 0,
  status: 'idle',
};

export function useViewerTimeline(
  companyId: string | null,
  productId: string | null,
): UseViewerTimelineResult {
  const [state, setState] = useState<UseViewerTimelineResult>(() => {
    if (companyId && productId) {
      const cached = sessionCache.get(cacheKey(companyId, productId));
      if (cached) return cached;
      return { ...EMPTY_RESULT, status: 'loading' };
    }
    return EMPTY_RESULT;
  });

  const fetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!companyId || !productId) {
      setState(EMPTY_RESULT);
      fetchedRef.current = null;
      return;
    }

    const key = cacheKey(companyId, productId);

    if (fetchedRef.current === key) return;

    const cached = sessionCache.get(key);
    if (cached) {
      fetchedRef.current = key;
      setState(cached);
      return;
    }

    let cancelled = false;
    setState({ ...EMPTY_RESULT, status: 'loading' });

    async function load() {
      try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(
          `${baseUrl}/api/viewer/${encodeURIComponent(companyId!)}/` +
            `${encodeURIComponent(productId!)}/timeline`,
        );
        if (cancelled) return;

        if (response.status === 404) {
          const result: UseViewerTimelineResult = { ...EMPTY_RESULT, status: 'missing' };
          sessionCache.set(key, result);
          fetchedRef.current = key;
          setState(result);
          return;
        }

        if (!response.ok) {
          const result: UseViewerTimelineResult = { ...EMPTY_RESULT, status: 'error' };
          fetchedRef.current = key;
          setState(result);
          return;
        }

        const json = (await response.json()) as {
          success?: boolean;
          data?: {
            steps?: TimelineStep[];
            visibilityAssignments?: StepObjectVisibility[];
            lastFrame?: number;
          };
        };

        if (cancelled) return;

        const data = json?.data;
        const result: UseViewerTimelineResult = {
          steps: Array.isArray(data?.steps) ? data!.steps : [],
          visibilityAssignments: Array.isArray(data?.visibilityAssignments)
            ? data!.visibilityAssignments
            : [],
          lastFrame:
            typeof data?.lastFrame === 'number' && Number.isFinite(data.lastFrame)
              ? data.lastFrame
              : 0,
          status: 'ready',
        };
        sessionCache.set(key, result);
        fetchedRef.current = key;
        setState(result);
      } catch {
        if (cancelled) return;
        const result: UseViewerTimelineResult = { ...EMPTY_RESULT, status: 'error' };
        fetchedRef.current = key;
        setState(result);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [companyId, productId]);

  return state;
}

/**
 * Test-only helper: clears the session cache so unit tests can run in isolation.
 * Not exported in production bundles by convention — left as a named export
 * for explicit opt-in by test files only.
 */
export function __resetViewerTimelineCacheForTests(): void {
  sessionCache.clear();
}
